import { Component } from '@theme/component';

/**
 * @typedef {{ toggle: HTMLButtonElement, panel: HTMLElement, liveRegion: HTMLElement }} RoomAddonsRefs
 */

/** @extends {Component<RoomAddonsRefs>} */
export class RoomAddonsComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['toggle', 'panel'];

    togglePanel() {
        const { toggle, panel } = this.refs;
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
        this.classList.toggle('is-open', !isOpen);
    }

    /** @param {Event} event */
    toggleAddon(event) {
        const addBtn = event.target;
        if (!(addBtn instanceof HTMLButtonElement)) return;

        const card = addBtn.closest('.room-info__addon-card');
        if (!(card instanceof HTMLElement)) return;

        const isSelected = card.classList.toggle('is-selected');
        addBtn.setAttribute('aria-pressed', String(isSelected));
        const nextLabel = isSelected ? addBtn.dataset.removeLabel : addBtn.dataset.addLabel;
        if (nextLabel) addBtn.setAttribute('aria-label', nextLabel);

        this.#announce(card, isSelected);
        this.#dispatchAddonChange();
    }

    /** @param {Event} event */
    handleSwatchChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;

        const card = input.closest('.room-info__addon-card');
        if (!(card instanceof HTMLElement)) return;

        card.querySelectorAll('.room-info__addon-card-swatch--active').forEach((el) => {
            el.classList.remove('room-info__addon-card-swatch--active');
        });

        const activeLabel = input.closest('.room-info__addon-card-swatch');
        activeLabel?.classList.add('room-info__addon-card-swatch--active');

        card.dataset.addonVariantId = input.dataset.addonVariantId || '';
        card.dataset.addonPrice = input.dataset.addonPrice || '';
        card.dataset.addonCompare = input.dataset.addonCompare || '';

        this.#updateAddonCardUI(card, input);

        if (card.classList.contains('is-selected')) {
            this.#dispatchAddonChange();
        }
    }

    /**
     * @param {HTMLElement} card
     * @param {HTMLInputElement} input
     */
    #updateAddonCardUI(card, input) {
        const img = card.querySelector('.room-info__addon-card-img');
        const imgUrl = input.dataset.addonImage;
        if (img instanceof HTMLImageElement && imgUrl) {
            img.src = imgUrl;
            img.removeAttribute('srcset');
        }

        const sale = card.querySelector('.room-info__addon-card-sale');
        const compare = card.querySelector('.room-info__addon-card-compare');
        const priceStr = input.dataset.addonPriceDisplay;
        const compareStr = input.dataset.addonCompareDisplay;
        const hasDiscount = input.dataset.addonHasDiscount === 'true';

        if (sale && priceStr) sale.textContent = priceStr;
        if (compare instanceof HTMLElement) {
            if (hasDiscount && compareStr) {
                compare.textContent = compareStr;
                compare.hidden = false;
            } else {
                compare.textContent = '';
                compare.hidden = true;
            }
        }
    }

    /**
     * @param {HTMLElement} card
     * @param {boolean} isSelected
     */
    #announce(card, isSelected) {
        const region = this.refs.liveRegion;
        if (!region) return;
        const title = card.dataset.addonTitle || 'Add-on';
        region.textContent = isSelected ? `${title} added` : `${title} removed`;
    }

    #dispatchAddonChange() {
        this.dispatchEvent(new CustomEvent('room:addon-change', { bubbles: true }));
    }
}

if (!customElements.get('room-addons-component')) {
    customElements.define('room-addons-component', RoomAddonsComponent);
}
